import { useEffect, useMemo, useState } from 'react';
import { Building2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  Description,
  Skeleton,
  Spinner,
  Tabs,
  useOverlayState,
} from '@heroui/react';
import {
  availableSpotsKeys,
  createBulkSpotUsageRequest,
  createSpotOwnerRequest,
  createSpotUsage,
  getAllSpotsByCondominium,
  getAvailableSpots,
  type AvailableSpot,
  type GetAvailableSpotsParams,
} from '../../../api/available-spots';
import {
  getNotifications,
  notificationsKeys,
  type AppNotification,
} from '../../../api/notifications';
import {
  getRecentMisuseReport,
  misuseReportKeys,
  type RecentMisuseReport,
} from '../../../api/misuse-reports';
import { parseNotificationMetadata } from '../../notifications/utils/notification-display';
import type { ParkingSpot } from '../../../api/condominiums';
import { cancelSpotUsage, getMyParkingSpots, getMySpotUsages, myParkingSpotsKeys } from '../../../api/my-parking-spots';
import { FloorPlanDrawer } from '../../../components/floor-plan/floor-plan-drawer';
import {
  floorPlanRectFromLocation,
  type FloorPlanLocationFields,
} from '../../../components/floor-plan/floor-plan-utils';
import { PageHeader } from '../../../components/page/page-header';
import { PageFilterButton } from '../../../components/ui/page-filter-button';
import { primaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { TabCountBadge } from '../../../components/ui/tab-count-badge';
import { useAuth } from '../../../hooks/useAuth';
import { useCurrentCondominium } from '../../../hooks/useCurrentCondominium';
import { showErrorFromUnknown, showSuccess } from '../../../lib/toast';
import { invalidateParkingLists, parkingListQueryOptions } from '../../../lib/parking-query';
import { dateOnlyToUtcEnd, dateOnlyToUtcStart } from '../../../utils/date-utils';
import {
  AvailableSpotsFiltersDrawer,
  useAvailableSpotsFiltersCount,
} from '../components/available-spots-filters-drawer';
import type { AvailableSpotsFiltersState } from '../components/available-spots-filters-form';
import { AvailableSpotsSection } from '../components/available-spots-section';
import { BulkRequestDrawer } from '../components/bulk-request-drawer';
import { BulkRequestPromo } from '../components/bulk-request-promo';
import { OccupiedSpotsSection } from '../components/occupied-spots-section';
import { MisuseReportAlert } from '../components/misuse-report-alert';
import { ReportMisuseDrawer } from '../components/report-misuse-drawer';
import { ReportMisusePromo } from '../components/report-misuse-promo';
import { RequestUsageDrawer } from '../components/request-usage-drawer';
import { createDefaultFilters } from '../utils/available-spots-filter-utils';

const MISUSE_ALERT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function misuseReportFromInbox(notifications: AppNotification[]): RecentMisuseReport | null {
  const cutoff = Date.now() - MISUSE_ALERT_MAX_AGE_MS;
  const latest = notifications
    .filter(
      (notification) =>
        notification.type === 'SPOT_MISUSE_REPORT' &&
        new Date(notification.createdAt).getTime() >= cutoff,
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  if (!latest) return null;

  const meta = parseNotificationMetadata(latest.metadata);
  const id = meta?.reportId ?? latest.publicId;
  const resolved = notifications.some((notification) => {
    if (notification.type !== 'SPOT_MISUSE_RESOLVED') return false;
    return parseNotificationMetadata(notification.metadata)?.reportId === id;
  });
  if (resolved) return null;

  return {
    id,
    spotId: meta?.spotId ?? '',
    spotCode: meta?.spotCode ?? '',
    licensePlate: meta?.licensePlate ?? '',
    photoUrl: meta?.photoUrl ?? null,
    notes: null,
    createdAt: latest.createdAt,
    reporterFirebaseUid: meta?.reporterFirebaseUid ?? '',
  };
}

function buildApiFilters(filters: AvailableSpotsFiltersState): GetAvailableSpotsParams {
  const params: GetAvailableSpotsParams = {
    startAt: dateOnlyToUtcStart(filters.startDate),
    endAt: dateOnlyToUtcEnd(filters.endDate),
  };

  if (filters.filterBorrow && !filters.filterRent) {
    params.allowBorrow = true;
  } else if (!filters.filterBorrow && filters.filterRent) {
    params.allowRent = true;
  }

  return params;
}

export default function AvailableSpotsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authUser } = useAuth();
  const { condominium, condominiums, label: condominiumLabel, isLoading: isCondominiumsLoading } =
    useCurrentCondominium();
  const condominiumId = condominium?.id ?? '';

  const requestDrawer = useOverlayState();
  const bulkDrawer = useOverlayState();
  const occupiedRequestDrawer = useOverlayState();
  const misuseDrawer = useOverlayState();
  const filtersDrawer = useOverlayState();
  const locationDrawer = useOverlayState();

  const [selectedTab, setSelectedTab] = useState(
    () => (searchParams.get('tab') === 'occupied' ? 'occupied' : 'available'),
  );
  const [filters, setFilters] = useState<AvailableSpotsFiltersState>(() => createDefaultFilters());
  const [selectedSpot, setSelectedSpot] = useState<AvailableSpot | null>(null);
  const [occupiedSpot, setOccupiedSpot] = useState<ParkingSpot | null>(null);
  const [locationSpot, setLocationSpot] = useState<
    (FloorPlanLocationFields & { code: string }) | null
  >(null);

  const activeFilterCount = useAvailableSpotsFiltersCount(filters);
  const apiFilters = useMemo(() => buildApiFilters(filters), [filters]);

  useEffect(() => {
    setSelectedTab('available');
    setSelectedSpot(null);
  }, [condominiumId]);

  const availableQuery = useQuery({
    queryKey: availableSpotsKeys.available(condominiumId, apiFilters),
    queryFn: () => getAvailableSpots(condominiumId, apiFilters),
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
  });

  const allSpotsQuery = useQuery({
    queryKey: availableSpotsKeys.allSpots(condominiumId),
    queryFn: () => getAllSpotsByCondominium(condominiumId),
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
  });

  const myUsagesQuery = useQuery({
    queryKey: [...availableSpotsKeys.myUsages(), condominiumId],
    queryFn: getMySpotUsages,
    enabled: !!condominiumId && allSpotsQuery.isSuccess,
    ...parkingListQueryOptions,
    select: (usages) => {
      const spotIds = new Set((allSpotsQuery.data ?? []).map((spot) => spot.id));
      return usages.filter((usage) => spotIds.has(usage.spotId));
    },
  });

  const mySpotsQuery = useQuery({
    queryKey: myParkingSpotsKeys.list(condominiumId),
    queryFn: getMyParkingSpots,
    enabled: !!condominiumId,
    ...parkingListQueryOptions,
  });

  const recentMisuseQuery = useQuery({
    queryKey: misuseReportKeys.recent(condominiumId),
    queryFn: () => getRecentMisuseReport(condominiumId),
    enabled: !!condominiumId,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const inboxQuery = useQuery({
    queryKey: notificationsKeys.list(),
    queryFn: getNotifications,
    enabled: !!condominiumId && recentMisuseQuery.isError,
    refetchInterval: 30_000,
  });

  const latestMisuseReport =
    recentMisuseQuery.data ??
    (recentMisuseQuery.isError ? misuseReportFromInbox(inboxQuery.data ?? []) : null);

  const myOwnedSpots = useMemo(
    () =>
      (mySpotsQuery.data ?? [])
        .filter((spot) => !spot.condominiumId || spot.condominiumId === condominiumId)
        .map((spot) => ({ id: spot.spotId, code: spot.spotCode })),
    [mySpotsQuery.data, condominiumId],
  );

  const mySpotIds = useMemo(
    () => new Set(myOwnedSpots.map((spot) => spot.id)),
    [myOwnedSpots],
  );

  const invalidateLists = () => invalidateParkingLists(queryClient);

  const createUsageMutation = useMutation({
    mutationFn: createSpotUsage,
    onSuccess: () => {
      showSuccess('Solicitação enviada. Aguardando aprovação do proprietário.');
      invalidateLists();
    },
    onError: showErrorFromUnknown,
  });

  const bulkRequestMutation = useMutation({
    mutationFn: createBulkSpotUsageRequest,
    onSuccess: () => {
      showSuccess('Solicitação em massa enviada. Você será notificado se alguém aceitar.');
      invalidateLists();
    },
    onError: showErrorFromUnknown,
  });

  const spotOwnerRequestMutation = useMutation({
    mutationFn: createSpotOwnerRequest,
    onSuccess: () => {
      showSuccess('Pedido enviado ao proprietário da vaga.');
    },
    onError: showErrorFromUnknown,
  });

  const cancelUsageMutation = useMutation({
    mutationFn: cancelSpotUsage,
    onSuccess: () => {
      showSuccess('Solicitação cancelada.');
      invalidateLists();
    },
    onError: showErrorFromUnknown,
  });

  const openAvailability = (spot: AvailableSpot) => {
    setSelectedSpot(spot);
    requestDrawer.open();
  };

  const openRequest = (spot: AvailableSpot) => {
    if (spot.isMine) return;
    openAvailability(spot);
  };

  const openLocation = (spot: AvailableSpot | ParkingSpot) => {
    setLocationSpot(spot);
    locationDrawer.open();
  };

  const openOccupiedRequest = (spot: ParkingSpot) => {
    if (mySpotIds.has(spot.id)) return;
    setOccupiedSpot(spot);
    occupiedRequestDrawer.open();
  };

  const openReportMisuse = () => {
    misuseDrawer.open();
  };

  const handleCancelFromDrawer = async (usageId: string) => {
    await cancelUsageMutation.mutateAsync(usageId);
  };

  const availableSpots = availableQuery.data ?? [];
  const occupiedCount = useMemo(() => {
    const availableIds = new Set(availableSpots.map((s) => s.id));
    return (allSpotsQuery.data ?? []).filter((s) => !availableIds.has(s.id)).length;
  }, [availableSpots, allSpotsQuery.data]);

  const isLoadingInitial =
    isCondominiumsLoading ||
    (!!condominiumId && availableQuery.isLoading && !availableQuery.data);

  const showFilters = !isCondominiumsLoading && condominiums.length > 0 && !!condominiumId;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Vagas disponíveis"
        description={
          condominiumLabel
            ? `Vagas para empréstimo ou aluguel em ${condominiumLabel}.`
            : 'Busque vagas para empréstimo ou aluguel no seu condomínio.'
        }
        actions={
          showFilters ? (
                <PageFilterButton
                  activeFilterCount={activeFilterCount}
                  onPress={filtersDrawer.open}
                  tourId="available-spots-filters"
                />
          ) : null
        }
      />

      {latestMisuseReport ? (
        <MisuseReportAlert
          report={latestMisuseReport}
          isReporter={latestMisuseReport.reporterFirebaseUid === authUser?.uid}
          onResolved={() => {
            queryClient.setQueryData(misuseReportKeys.recent(condominiumId), null);
            void queryClient.invalidateQueries({ queryKey: misuseReportKeys.all });
            void queryClient.invalidateQueries({ queryKey: notificationsKeys.list() });
            void queryClient.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
            showSuccess('Mau uso encerrado.');
          }}
        />
      ) : null}

      {isCondominiumsLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      ) : condominiums.length === 0 ? (
        <Card>
          <Card.Content className="flex flex-col items-center gap-3 py-12 text-center">
            <Building2 size={40} className="text-muted" />
            <Description>Você não está associado a nenhum condomínio.</Description>
            <Description className="text-sm">
              Entre em contato com o gestor para solicitar associação.
            </Description>
          </Card.Content>
        </Card>
      ) : !condominiumId ? (
        <Card>
          <Card.Content className="py-12 text-center">
            <Description>Selecione um condomínio no menu do usuário para continuar.</Description>
          </Card.Content>
        </Card>
      ) : (
        <>
          {availableQuery.isError ? (
            <StatusCallout
              status="danger"
              title="Erro ao carregar vagas"
              action={
                <Button size="sm" variant="secondary" onPress={() => availableQuery.refetch()}>
                  Tentar novamente
                </Button>
              }
            >
              Tente novamente em instantes.
            </StatusCallout>
          ) : isLoadingInitial ? (
            <div className="flex h-48 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <Tabs
              selectedKey={selectedTab}
              onSelectionChange={(key) => {
                const nextTab = String(key);
                setSelectedTab(nextTab);
                const nextParams = new URLSearchParams(searchParams);
                if (nextTab === 'occupied') nextParams.set('tab', 'occupied');
                else nextParams.delete('tab');
                setSearchParams(nextParams, { replace: true });
              }}
            >
              <Tabs.ListContainer>
                <Tabs.List aria-label="Vagas do condomínio" className="p-1.5" data-tour="spots-tabs">
                  <Tabs.Tab id="available" className={primaryPageTabClassName}>
                    <span className="inline-flex items-center gap-2">
                      Disponíveis
                      <TabCountBadge count={availableSpots.length} color="success" />
                    </span>
                    <Tabs.Indicator />
                  </Tabs.Tab>
                  <Tabs.Tab id="occupied" className={primaryPageTabClassName}>
                    <span className="inline-flex items-center gap-2">
                      Ocupadas
                      <TabCountBadge count={occupiedCount} color="danger" />
                    </span>
                    <Tabs.Indicator />
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs.ListContainer>

              <Tabs.Panel id="available" className="flex flex-col gap-4 pt-3">
                <AvailableSpotsSection
                  spots={availableSpots}
                  onRequest={openRequest}
                  onViewLocation={openLocation}
                  onViewAvailability={openAvailability}
                />
                <BulkRequestPromo onOpenBulkRequest={bulkDrawer.open} />
                {myOwnedSpots.length > 0 ? (
                  <ReportMisusePromo onOpenReport={openReportMisuse} />
                ) : null}
              </Tabs.Panel>

              <Tabs.Panel id="occupied" className="flex flex-col gap-4 pt-3">
                {allSpotsQuery.isLoading ? (
                  <div className="flex h-40 items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <OccupiedSpotsSection
                    allSpots={allSpotsQuery.data ?? []}
                    availableSpots={availableSpots}
                    mySpotIds={mySpotIds}
                    onViewLocation={openLocation}
                    onRequest={openOccupiedRequest}
                  />
                )}
                {myOwnedSpots.length > 0 ? (
                  <ReportMisusePromo onOpenReport={openReportMisuse} />
                ) : null}
              </Tabs.Panel>
            </Tabs>
          )}
        </>
      )}

      {filtersDrawer.isOpen ? (
        <AvailableSpotsFiltersDrawer
          state={filtersDrawer}
          appliedFilters={filters}
          onApply={setFilters}
        />
      ) : null}

      {requestDrawer.isOpen ? (
        <RequestUsageDrawer
          state={requestDrawer}
          spot={selectedSpot}
          myUsages={myUsagesQuery.data ?? []}
          onSubmit={(request) => createUsageMutation.mutateAsync(request)}
          onCancelUsage={handleCancelFromDrawer}
        />
      ) : null}

      {bulkDrawer.isOpen ? (
        <BulkRequestDrawer
          state={bulkDrawer}
          onSubmit={(payload) => bulkRequestMutation.mutateAsync(payload)}
        />
      ) : null}

      {occupiedRequestDrawer.isOpen ? (
        <BulkRequestDrawer
          state={occupiedRequestDrawer}
          variant="spot"
          spotCode={occupiedSpot?.code}
          onSubmit={async (payload) => {
            if (!occupiedSpot) return;
            await spotOwnerRequestMutation.mutateAsync({
              ...payload,
              spotId: occupiedSpot.id,
            });
          }}
        />
      ) : null}

      {misuseDrawer.isOpen ? (
        <ReportMisuseDrawer
          state={misuseDrawer}
          spots={myOwnedSpots}
          onSubmitted={(notifiedCount) => {
            void queryClient.invalidateQueries({ queryKey: misuseReportKeys.all });
            void queryClient.invalidateQueries({ queryKey: notificationsKeys.unreadCount() });
            showSuccess(
              notifiedCount > 0
                ? `Relato enviado. ${notifiedCount} morador(es) foram notificados.`
                : 'Relato enviado.',
            );
          }}
        />
      ) : null}

      <FloorPlanDrawer
        state={locationDrawer}
        title={
          locationSpot ? `Localização da vaga ${locationSpot.code}` : 'Localização da vaga'
        }
        subtitle="Visualização da vaga na planta do condomínio"
        rect={locationSpot ? floorPlanRectFromLocation(locationSpot) : null}
      />
    </div>
  );
}
